import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyValue, FilterGrid, Form, ServiceAreaPill, StatusBadge } from '@/components/ui';
import type { StatusVariant } from '../../../utils/statusUtils';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import Toast from '../../../components/Toast';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { areasService, type ForemanArea, type ServiceArea } from '../../../services/areasService';
import {
  DEFECT_SEVERITIES,
  DEFECT_STATUSES,
  DEFECT_STATUS_LABEL,
  vehicleDefectsService,
  type DefectSeverity,
  type DefectStatus,
  type OpenDefectRow
} from '../../../services/vehicleDefectsService';

export interface OpenDefectsReportFilters {
  severity: DefectSeverity[];
  status: DefectStatus[];
  serviceAreaIds: string[];
  foremanAreaIds: string[];
  unit: string;
}

/** The report opens on open Critical defects (EQ-30). */
export const DEFAULT_OPEN_DEFECTS_FILTERS: OpenDefectsReportFilters = {
  severity: ['Critical'],
  status: ['Open'],
  serviceAreaIds: [],
  foremanAreaIds: [],
  unit: ''
};

function listParam(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(key)
    .flatMap((v) => v.split(','))
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * Filters from the URL (`?report=equipment-open-defects&severity=Critical&serviceAreaId=16`), so
 * the Home widget can open the report pre-filtered. A filter the URL doesn't name keeps its default.
 */
export function filtersFromSearchParams(params: URLSearchParams): OpenDefectsReportFilters {
  const severity = listParam(params, 'severity').filter((v): v is DefectSeverity =>
    (DEFECT_SEVERITIES as string[]).includes(v)
  );
  const status = listParam(params, 'status').filter((v): v is DefectStatus => (DEFECT_STATUSES as string[]).includes(v));
  return {
    severity: params.has('severity') ? severity : DEFAULT_OPEN_DEFECTS_FILTERS.severity,
    status: params.has('status') ? status : DEFAULT_OPEN_DEFECTS_FILTERS.status,
    serviceAreaIds: listParam(params, 'serviceAreaId'),
    foremanAreaIds: listParam(params, 'foremanAreaId'),
    unit: params.get('unit')?.trim() ?? ''
  };
}

const SEVERITY_VARIANT: Record<DefectSeverity, StatusVariant> = {
  Critical: 'danger',
  High: 'warning',
  Medium: 'info',
  Low: 'secondary'
};

const STATUS_VARIANT: Record<DefectStatus, StatusVariant> = {
  Open: 'danger',
  Closed: 'secondary'
};

const SEVERITY_OPTIONS = DEFECT_SEVERITIES.map((value) => ({ value, label: value }));
const STATUS_OPTIONS = DEFECT_STATUSES.map((value) => ({ value, label: DEFECT_STATUS_LABEL[value] }));

function text(value: string | null | undefined): React.ReactNode {
  return value && value.trim() ? value : <EmptyValue />;
}

function recordsLabel(total: number): string {
  return `${total.toLocaleString()} ${total === 1 ? 'record' : 'records'}`;
}

function todayPacific(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Vancouver' }).format(new Date());
}

interface EquipmentOpenDefectsReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  /** Filters from the URL; when given, the report runs straight away. */
  initialFilters?: OpenDefectsReportFilters;
}

/**
 * Equipment > Open Defects (EQ-30). One row per defect on the units the caller can see. Reads the
 * same server query as the Home Critical Defects widget and the Equipment badge, so the numbers
 * agree. Defaults to Critical, Open.
 */
const EquipmentOpenDefectsReportView: React.FC<EquipmentOpenDefectsReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  initialFilters
}) => {
  const [filters, setFilters] = useState<OpenDefectsReportFilters>(initialFilters ?? DEFAULT_OPEN_DEFECTS_FILTERS);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [rows, setRows] = useState<OpenDefectRow[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [hasRun, setHasRun] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const [sortField, setSortField] = useState('reportedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  /** The filters the table on screen was run with; Export uses these, not unsaved edits. */
  const ranWith = useRef<OpenDefectsReportFilters>(filters);
  const request = useRef(0);

  useEffect(() => {
    let cancelled = false;
    areasService
      .getForemanAreas()
      .then((areas) => {
        if (!cancelled) setForemanAreas(Array.isArray(areas) ? areas : []);
      })
      .catch(() => {
        // Not fatal: the Foreman Area filter is just empty.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const foremanAreaOptions = useMemo(() => {
    const chosen = new Set(filters.serviceAreaIds);
    return foremanAreas
      .filter((fa) => chosen.size === 0 || chosen.has(String(fa.serviceAreaId)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [foremanAreas, filters.serviceAreaIds]);

  const toQuery = (f: OpenDefectsReportFilters) => ({
    severity: f.severity,
    status: f.status,
    serviceAreaId: f.serviceAreaIds,
    foremanAreaId: f.foremanAreaIds,
    unit: f.unit
  });

  const run = async (
    page = 1,
    limit = pagination.limit,
    sort: { field: string; direction: 'asc' | 'desc' } = { field: sortField, direction: sortDirection },
    using: OpenDefectsReportFilters = filters
  ) => {
    const id = ++request.current;
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await vehicleDefectsService.listOpen(toQuery(using), {
        page,
        limit,
        sortField: sort.field,
        sortDirection: sort.direction
      });
      if (id !== request.current) return;
      ranWith.current = using;
      setRows(response.data);
      setPagination(response.pagination);
      setHasRun(true);
    } catch (error) {
      if (id !== request.current) return;
      // STATE-02: say so, with a way to try again; never show an empty list for an error.
      setErrorMessage(error instanceof Error ? error.message : 'Could not load defects.');
      setRows([]);
      setPagination((p) => ({ ...p, total: 0, totalPages: 0 }));
    } finally {
      if (id === request.current) setLoadingPreview(false);
    }
  };

  // Opened from a link (Home widget): run with the URL's filters straight away.
  useEffect(() => {
    if (initialFilters) run(1, pagination.limit, { field: sortField, direction: sortDirection }, initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (field: string) => {
    const direction = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortDirection(direction);
    run(1, pagination.limit, { field, direction }, ranWith.current);
  };

  const handleExport = async () => {
    setLoadingExport(true);
    setExportError(null);
    try {
      await vehicleDefectsService.exportOpenCsv(
        toQuery(ranWith.current),
        { sortField, sortDirection },
        `Open Defects_${todayPacific().replace(/-/g, '_')}.csv`
      );
      setToastMessage('Open Defects report exported.');
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Could not export defects.');
    } finally {
      setLoadingExport(false);
    }
  };

  const columns = [
    {
      label: 'Unit',
      key: 'unitNumber',
      sortable: true,
      width: '110px',
      render: (row: OpenDefectRow) => (
        <Link to={`/equipment/${row.vehicleId}?tab=defects`} title={row.unitDescription ?? row.unitNumber}>
          {row.unitNumber}
        </Link>
      )
    },
    {
      label: 'Service Area',
      key: 'serviceArea',
      sortable: true,
      width: '150px',
      render: (row: OpenDefectRow) => <ServiceAreaPill value={row.serviceArea} />
    },
    {
      label: 'Foreman Area',
      key: 'foremanArea',
      sortable: true,
      width: '150px',
      render: (row: OpenDefectRow) => text(row.foremanArea?.name)
    },
    {
      label: 'Category',
      key: 'category',
      sortable: true,
      width: '160px',
      render: (row: OpenDefectRow) => text(row.category)
    },
    {
      label: 'Description',
      key: 'description',
      sortable: true,
      width: '320px',
      render: (row: OpenDefectRow) => <span className="tw-whitespace-pre-line">{text(row.description)}</span>
    },
    {
      label: 'Severity',
      key: 'severity',
      sortable: true,
      width: '110px',
      render: (row: OpenDefectRow) => (
        <StatusBadge status={row.severity} label={row.severity} variant={SEVERITY_VARIANT[row.severity]} />
      )
    },
    {
      label: 'Reported',
      key: 'reportedAt',
      sortable: true,
      width: '120px',
      render: (row: OpenDefectRow) => <span className="tw-tabular-nums">{row.reportedDate}</span>
    },
    {
      label: 'Reported By',
      key: 'reportedBy',
      sortable: true,
      width: '160px',
      render: (row: OpenDefectRow) => text(row.reportedBy)
    },
    {
      label: 'Status',
      key: 'status',
      sortable: true,
      width: '220px',
      render: (row: OpenDefectRow) => (
        <StatusBadge status={row.status} label={DEFECT_STATUS_LABEL[row.status]} variant={STATUS_VARIANT[row.status]} />
      )
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0 }}>
        {errorMessage && (
          <Alert variant="danger" className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <span>{errorMessage}</span>
            <Button size="sm" variant="outline-danger" onClick={() => run(1, pagination.limit, undefined, ranWith.current)}>
              Retry
            </Button>
          </Alert>
        )}
        {exportError && (
          <Alert variant="danger" className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <span>{exportError}</span>
            <Button size="sm" variant="outline-danger" onClick={handleExport}>
              Retry
            </Button>
          </Alert>
        )}
        <ReportParametersCard
          onGenerate={() => run(1)}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          disableExport
          hideExportButton
          generateButtonText="Run Report"
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Severity</Form.Label>
              <MultiSelectDropdown
                options={SEVERITY_OPTIONS}
                value={filters.severity}
                onChange={(selected) => setFilters({ ...filters, severity: selected.map(String) as DefectSeverity[] })}
                formatOption={(o) => o.label}
                getValue={(o) => o.value}
                placeholder="All Severities"
                preserveOptionsOrder
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Status</Form.Label>
              <MultiSelectDropdown
                options={STATUS_OPTIONS}
                value={filters.status}
                onChange={(selected) => setFilters({ ...filters, status: selected.map(String) as DefectStatus[] })}
                formatOption={(o) => o.label}
                getValue={(o) => o.value}
                placeholder="Open"
                preserveOptionsOrder
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <MultiSelectDropdown
                options={serviceAreas}
                value={filters.serviceAreaIds}
                onChange={(selected) =>
                  setFilters({ ...filters, serviceAreaIds: selected.map(String), foremanAreaIds: [] })
                }
                formatOption={(area) => area.name}
                getValue={(area) => String(area.id)}
                placeholder="All Service Areas"
                disabled={loadingServiceAreas}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Foreman Area</Form.Label>
              <MultiSelectDropdown
                options={foremanAreaOptions}
                value={filters.foremanAreaIds}
                onChange={(selected) => setFilters({ ...filters, foremanAreaIds: selected.map(String) })}
                formatOption={(fa) => fa.name}
                getValue={(fa) => String(fa.id)}
                placeholder="All Foreman Areas"
                noOptionsMessage="No foreman areas"
              />
            </Form.Group>
            <Form.Group className="tw-mb-1" controlId="open-defects-unit">
              <Form.Label className="tw-text-sm tw-mb-1">Unit</Form.Label>
              <Form.Control
                size="sm"
                type="search"
                placeholder="Unit number or description"
                value={filters.unit}
                onChange={(e) => setFilters({ ...filters, unit: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') run(1);
                }}
              />
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">Open Defects ({recordsLabel(pagination.total)})</h6>
            {pagination.total > 0 && (
              <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={loadingExport}>
                <i className="bi bi-download tw-mr-1" aria-hidden="true"></i>
                {loadingExport ? 'Exporting…' : 'Export CSV'}
              </Button>
            )}
          </Card.Header>
          <Card.Body
            className="tw-p-0"
            style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}
          >
            <ReportPreviewTable
              columns={columns}
              data={rows}
              pagination={{
                ...pagination,
                onPageChange: (page: number) => run(page, pagination.limit, undefined, ranWith.current),
                onPageSizeChange: (size: number) => run(1, size, undefined, ranWith.current)
              }}
              loading={loadingPreview}
              emptyMessage={
                hasRun && !errorMessage ? 'No defects match these filters.' : "Click 'Run Report' to load defects."
              }
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              isFiltersCollapsed={isFiltersCollapsed}
            />
          </Card.Body>
        </Card>
      </div>

      <Toast show={!!toastMessage} onClose={() => setToastMessage('')} message={toastMessage} type="success" />
    </div>
  );
};

export default EquipmentOpenDefectsReportView;
